import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { keys, useAlerts, useSocialKeywords, useSocialStatus } from '../../api/queries';
import { useSocialAlerts } from '../../api/socket';
import { Ic } from '../../components/Ic';
import { Empty } from '../../components/Empty';
import { relTime } from '../../lib/time';
import type { Alert, SocialAlert } from '../../api/types';

const isSocial = (a: Alert): a is SocialAlert => a.type === 'social_raffle';

/** The Graph APIs give us ids, not handles, so an account is identified by the
 *  id it was configured with. */
const accountKey = (a: SocialAlert) => (a.platform === 'instagram' ? a.account_id : a.page_id);
const alertText = (a: SocialAlert) => (a.platform === 'instagram' ? a.caption : a.message);
const alertLink = (a: SocialAlert) => (a.platform === 'instagram' ? a.permalink : a.permalink_url);

function PlatIcon({ platform }: { platform: 'instagram' | 'facebook' }) {
  return (
    <div className={`plat ${platform === 'instagram' ? 'plat-ig' : 'plat-fb'}`}>
      <Ic.globe className="ic" />
    </div>
  );
}

function AccountCard({
  id,
  platform,
  hits,
  lastHit,
}: {
  id: string;
  platform: 'instagram' | 'facebook';
  hits: number;
  lastHit: number | null;
}) {
  return (
    <div className="soc">
      <div className="sh">
        <PlatIcon platform={platform} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="handle">{id}</div>
          <div className="fol">{platform === 'instagram' ? 'Instagram' : 'Facebook'}</div>
        </div>
        {/* The prototype had a "watching" toggle. Accounts come from
            INSTAGRAM_ACCOUNTS / FACEBOOK_PAGE_IDS in .env and there is no write
            path, so showing a switch would imply control that doesn't exist. */}
        <span className="pill green dot">watching</span>
      </div>
      <div className="smetrics">
        <div>
          <div className="k">Raffle hits</div>
          <div className="v" style={{ color: 'var(--teal)' }}>
            {hits}
          </div>
        </div>
        <div>
          <div className="k">Last hit</div>
          <div className="v" style={{ fontSize: 13 }}>
            {lastHit ? relTime(lastHit) : '—'}
          </div>
        </div>
      </div>
    </div>
  );
}

export function SocialView() {
  const queryClient = useQueryClient();
  const { data: status, isLoading } = useSocialStatus();
  const { data: keywords } = useSocialKeywords();
  const { data: alerts } = useAlerts();

  // Same reasoning as the sitemap feed: the stored record and the socket
  // payload differ, so refetch rather than hold a local copy.
  useSocialAlerts(() => {
    void queryClient.invalidateQueries({ queryKey: keys.alerts });
  });

  const socialAlerts = (alerts ?? []).filter(isSocial);

  // hits / lastHit are derived from the alert history — no backend work needed.
  const byAccount = new Map<string, { hits: number; lastHit: number | null }>();
  for (const alert of socialAlerts) {
    const key = accountKey(alert);
    const prev = byAccount.get(key) ?? { hits: 0, lastHit: null };
    byAccount.set(key, {
      hits: prev.hits + 1,
      lastHit: Math.max(prev.lastHit ?? 0, alert.timestamp) || null,
    });
  }
  const statsFor = (id: string) => byAccount.get(id) ?? { hits: 0, lastHit: null };

  const igIds = status?.instagram_account_ids ?? [];
  const fbIds = status?.facebook_page_ids ?? [];
  const configured = igIds.length + fbIds.length;
  const totalHits = socialAlerts.length;

  return (
    <div className="view-pad">
      <div className="sec-head">
        <h2>Social Monitor</h2>
        <span className="desc">Instagram and Facebook raffle detection, every 30 minutes</span>
        <span className="spacer" />
        {/* Accounts are env config, so this points at where they're actually set. */}
        <Link className="btn ghost sm" to="/settings/environment">
          Configure accounts <Ic.arrowRight className="ico" />
        </Link>
      </div>

      <div className="kpi-grid cols-3" style={{ marginBottom: 16 }}>
        <div className="kpi">
          <div className="top">
            <Ic.globe className="ic" />
            <span className="label">Accounts watched</span>
          </div>
          <div className="value">{isLoading ? '—' : configured}</div>
          <div className="delta">
            {igIds.length} Instagram · {fbIds.length} Facebook
          </div>
        </div>
        <div className="kpi">
          <div className="top">
            <Ic.zap className="ic" />
            <span className="label">Raffle hits</span>
          </div>
          <div className="value" style={{ color: 'var(--teal)' }}>
            {totalHits}
          </div>
          <div className="delta">across stored alerts</div>
        </div>
        <div className="kpi">
          <div className="top">
            <Ic.tag className="ic" />
            <span className="label">Keywords</span>
          </div>
          <div className="value indigo">{keywords?.keywords.length ?? '—'}</div>
          <div className="delta">matched against every post</div>
        </div>
      </div>

      {!isLoading && configured === 0 && (
        <Empty
          icon={Ic.globe}
          title="No social accounts configured"
          detail="Set INSTAGRAM_ACCOUNTS and FACEBOOK_PAGE_IDS in the environment to start monitoring."
        />
      )}

      <div className="two-col">
        <div>
          {igIds.length > 0 && (
            <>
              <div className="sec-head">
                <h2 style={{ fontSize: 13.5 }}>Instagram</h2>
              </div>
              <div className="soc-grid" style={{ marginBottom: 18 }}>
                {igIds.map((id) => {
                  const s = statsFor(id);
                  return (
                    <AccountCard key={id} id={id} platform="instagram" hits={s.hits} lastHit={s.lastHit} />
                  );
                })}
              </div>
            </>
          )}
          {fbIds.length > 0 && (
            <>
              <div className="sec-head">
                <h2 style={{ fontSize: 13.5 }}>Facebook</h2>
              </div>
              <div className="soc-grid">
                {fbIds.map((id) => {
                  const s = statsFor(id);
                  return (
                    <AccountCard key={id} id={id} platform="facebook" hits={s.hits} lastHit={s.lastHit} />
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="pcard">
            <div className="head">
              <Ic.tag className="ic" />
              <h3>Detection keywords</h3>
            </div>
            <div className="body pad">
              <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
                A post is flagged when its caption contains any of these. Defined in the monitor
                modules, so this list is read-only.
              </div>
              <div className="kw-row">
                {keywords?.keywords.map((k) => (
                  <span key={k} className="kw">
                    {k}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="pcard">
            <div className="head">
              <Ic.history className="ic" />
              <h3>Detected posts</h3>
              <span className="spacer" />
              <span className="pill">
                <span className="live-dot" style={{ marginRight: 4 }} />
                live
              </span>
            </div>
            <div className="body">
              {socialAlerts.length === 0 && (
                <div className="frow" style={{ borderBottom: 'none' }}>
                  <div className="main">
                    <div className="m">Nothing detected yet.</div>
                  </div>
                </div>
              )}
              {socialAlerts.slice(0, 10).map((alert, i) => {
                const link = alertLink(alert);
                return (
                  <div key={`${accountKey(alert)}-${alert.timestamp}-${i}`} className="frow">
                    <PlatIcon platform={alert.platform} />
                    <div className="main">
                      <div className="t">
                        {link ? (
                          <a href={link} target="_blank" rel="noreferrer noopener" style={{ color: 'inherit' }}>
                            {alertText(alert)?.slice(0, 90) || 'Untitled post'}
                          </a>
                        ) : (
                          alertText(alert)?.slice(0, 90) || 'Untitled post'
                        )}
                      </div>
                      <div className="m">
                        <span>{accountKey(alert)}</span>
                        <span className="muted">·</span>
                        <span>{relTime(alert.timestamp)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
